/** Customer attribution is independent of future advertising-provider integrations. */
export type MarketingTouch = {
  source: string;
  medium: string;
  campaign: string;
  campaignId: string;
  creativeId: string;
  adId: string;
  content: string;
  term: string;
  referrerHost: string;
  landingPath: string;
  capturedAt: string;
};
const clean = (v: unknown, max = 180) =>
  typeof v === "string"
    ? v
        .replace(/[\u0000-\u001f]/g, "")
        .trim()
        .slice(0, max)
    : "";
export function normalizeMarketingTouch(
  value: any,
  now = new Date().toISOString(),
): MarketingTouch | null {
  if (!value || typeof value !== "object") return null;
  const source = clean(value.source),
    referrerHost = clean(value.referrerHost);
  if (!source && !referrerHost) return null;
  return {
    source,
    medium: clean(value.medium),
    campaign: clean(value.campaign),
    campaignId: clean(value.campaignId),
    creativeId: clean(value.creativeId),
    adId: clean(value.adId),
    content: clean(value.content),
    term: clean(value.term),
    referrerHost: /^[a-z0-9.-]+$/i.test(referrerHost) ? referrerHost : "",
    landingPath: clean(value.landingPath, 300).split("?")[0],
    capturedAt:
      typeof value.capturedAt === "string" &&
      Number.isFinite(Date.parse(value.capturedAt))
        ? new Date(
            Math.min(Date.parse(value.capturedAt), Date.parse(now)),
          ).toISOString()
        : now,
  };
}
export function marketingChannel(touch: any): string {
  const source = String(
    touch?.source || touch?.referrerHost || "",
  ).toLowerCase();
  if (/google|adwords/.test(source)) return "Google";
  if (/instagram|(^|\W)ig($|\W)/.test(source)) return "Instagram";
  if (/facebook|meta|(^|\W)fb($|\W)/.test(source)) return "Meta / Facebook";
  if (/twitter|^x$|x\.com|t\.co/.test(source)) return "X";
  if (/thumbtack/.test(source)) return "Thumbtack";
  if (/nextdoor/.test(source)) return "Nextdoor";
  return touch?.source || touch?.referrerHost || "Source not recorded";
}
/** Never replace the original acquisition source as the customer progresses. */
export function mergeMarketingAttribution(existing: any, touch: any) {
  const normalized = normalizeMarketingTouch(touch);
  if (!normalized) return existing || null;
  return {
    firstTouch: existing?.firstTouch || normalized,
    lastTouch: normalized,
  };
}
